/**
 * S16 — Audit Event Drawer (right-side, 680 px).
 *
 * Inspects one ledger entry in full:
 *   · Event header with actor, timestamp, action, severity
 *   · Entity / target (clickable cross-link to its surface)
 *   · Hash chain neighbourhood: previous entry → this entry → next entry
 *     so auditors can see the immutability proof in context
 *   · Raw metadata (ip, id, prevHash, hash)
 *   · "Open target" CTA when the entity has a corresponding page
 */
import React, { useEffect, useMemo } from "react";
import {
  X, Clock, UserCog, MapPin, Hash, ArrowUpRight, ChevronUp, ChevronDown,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  EntityChip, SeverityPill, HashBadge,
} from "./components/AuditPrimitives";
import { resolveAuditTargetRoute } from "./auditTargets";

// An audit `action` value is treated as a narrative sentence when it
// looks like prose (has spaces and is longer than a verb code).
function looksLikeSentence(s) {
  return typeof s === "string" && s.length > 24 && s.includes(" ");
}

export default function AuditEventDrawer({ entry, entries, onClose, onTargetClick }) {
  useEffect(() => {
    if (!entry) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [entry, onClose]);

  const neighbourhood = useMemo(() => {
    if (!entry) return null;
    const i = entries.findIndex((e) => e.id === entry.id);
    return {
      newer: i > 0 ? entries[i - 1] : null,
      current: entry,
      older:  i < entries.length - 1 ? entries[i + 1] : null,
    };
  }, [entry, entries]);

  if (!entry) return null;

  const linkable = !!resolveAuditTargetRoute(entry);

  return (
    <div className="fixed inset-0 z-50" data-testid="audit-event-drawer" role="dialog" aria-modal="true">
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-ink-primary/40 backdrop-blur-[2px]"
        data-testid="audit-drawer-backdrop"
      />
      <aside
        className="absolute right-0 top-0 bottom-0 w-full sm:w-[680px] bg-raised shadow-e4 border-l border-border-subtle flex flex-col"
        style={{ animation: "slide-in-right 220ms ease-out" }}
      >
        <DrawerHeader entry={entry} onClose={onClose} linkable={linkable} onTargetClick={onTargetClick} />
        <DrawerBody entry={entry} neighbourhood={neighbourhood} />
        <DrawerFooter
          entry={entry}
          linkable={linkable}
          onClose={onClose}
          onTargetClick={onTargetClick}
        />
      </aside>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
function DrawerHeader({ entry: e, onClose, linkable, onTargetClick }) {
  return (
    <div className="border-b border-border-subtle px-5 pt-4 pb-4" data-testid="audit-drawer-header">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-meta font-mono text-ink-secondary tabular-nums">{e.id}</span>
            <EntityChip type={e.entityType} />
            <SeverityPill severity={e.severity} />
          </div>
          <h2
            className={cn(
              "mt-1 text-h2 text-ink-primary break-words",
              !e.narrative && !looksLikeSentence(e.action) && "font-mono break-all",
            )}
            data-testid="audit-drawer-action"
          >
            {e.narrative || e.action}
          </h2>
          {e.narrative && (
            <p className="mt-2 text-meta font-mono text-ink-secondary" data-testid="audit-drawer-action-code">
              {e.action}
            </p>
          )}
          <p className="mt-1 text-meta text-ink-secondary inline-flex items-center gap-3 flex-wrap">
            <span className="inline-flex items-center gap-1"><Clock size={11} /> {e.ts}</span>
            <span className="inline-flex items-center gap-1"><UserCog size={11} /> {e.actor}</span>
            <span className="inline-flex items-center gap-1"><MapPin size={11} /> {e.ip}</span>
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close drawer"
          className="h-8 w-8 inline-flex items-center justify-center rounded-md text-ink-secondary hover:bg-inset transition-colors"
          data-testid="audit-drawer-close"
        >
          <X size={16} />
        </button>
      </div>

      <div className="mt-3 flex items-center gap-2 flex-wrap text-meta">
        <span className="text-ink-secondary">Target</span>
        {linkable ? (
          <button
            type="button"
            onClick={() => onTargetClick(e)}
            className="inline-flex items-center gap-1 text-tralenex-700 hover:underline font-mono tabular-nums"
            data-testid="audit-drawer-target-link"
          >
            {e.entityRef} <ArrowUpRight size={11} />
          </button>
        ) : (
          <span className="font-mono tabular-nums text-ink-primary">{e.entityRef}</span>
        )}
      </div>
    </div>
  );
}

function DrawerBody({ entry: e, neighbourhood }) {
  return (
    <div className="flex-1 overflow-y-auto px-5 py-5 space-y-6" data-testid="audit-drawer-body">
      <Section icon={Hash} title="Hash chain neighbourhood">
        <p className="text-meta text-ink-secondary mb-3">
          Each entry&apos;s hash is derived from its content plus the previous
          entry&apos;s hash. Any tampering breaks the chain at the next
          verification.
        </p>
        <ChainNode
          variant="newer"
          label="Newer · next entry"
          entry={neighbourhood?.newer}
        />
        <ChainNode
          variant="current"
          label="Current entry"
          entry={neighbourhood?.current}
        />
        <ChainNode
          variant="older"
          label="Older · previous entry"
          entry={neighbourhood?.older}
        />
      </Section>

      <Section icon={Hash} title="Raw metadata">
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm" data-testid="audit-drawer-metadata">
          <MetaRow k="Entry id"      v={e.id} mono />
          <MetaRow k="Timestamp"     v={e.ts} mono />
          <MetaRow k="Actor"         v={e.actor} />
          <MetaRow k="Action"        v={e.action} mono />
          <MetaRow k="Entity type"   v={e.entityType} />
          <MetaRow k="Entity ref"    v={e.entityRef} mono />
          <MetaRow k="Severity"      v={e.severity} />
          <MetaRow k="Source IP"     v={e.ip} mono />
          <MetaRow k="Previous hash" v={e.prevHash} mono />
          <MetaRow k="This hash"     v={e.hash} mono />
        </dl>
      </Section>
    </div>
  );
}

function DrawerFooter({ entry, linkable, onClose, onTargetClick }) {
  return (
    <div className="border-t border-border-subtle px-5 py-3 bg-inset/30 flex items-center gap-2" data-testid="audit-drawer-footer">
      <button
        type="button"
        onClick={onClose}
        className="h-9 px-3 rounded-md text-sm font-medium text-ink-secondary hover:bg-inset transition-colors"
        data-testid="audit-drawer-action-close"
      >
        Close
      </button>
      <span className="flex-1" />
      {linkable && (
        <button
          type="button"
          onClick={() => onTargetClick(entry)}
          className="h-9 px-3 rounded-md text-sm font-semibold inline-flex items-center gap-2 bg-tralenex-600 text-white hover:bg-tralenex-700"
          data-testid="audit-drawer-action-open-target"
        >
          Open {entry.entityType} <ArrowUpRight size={13} />
        </button>
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
function Section({ icon: Icon, title, children }) {
  return (
    <section>
      <div className="flex items-center gap-2 mb-1.5">
        <Icon size={13} className="text-tralenex-700" />
        <p className="eyebrow">{title}</p>
      </div>
      <div className="text-sm">{children}</div>
    </section>
  );
}

function ChainNode({ variant, label, entry }) {
  if (!entry) {
    return (
      <div
        className="px-3 py-2 my-1 rounded-md border border-dashed border-border-subtle text-meta text-ink-disabled"
        data-testid={`audit-chain-${variant}-empty`}
      >
        {label} — no entry (chain terminus)
      </div>
    );
  }
  const isCurrent = variant === "current";
  return (
    <div
      data-testid={`audit-chain-${variant}`}
      className={cn(
        "px-3 py-2 my-1 rounded-md border flex items-start gap-3 transition-colors",
        isCurrent
          ? "border-tralenex-300 bg-tralenex-50/40"
          : "border-border-subtle bg-raised hover:bg-inset/30",
      )}
    >
      <span className="mt-1 shrink-0">
        {variant === "newer" ? <ChevronUp size={14} className="text-ink-secondary" />
         : variant === "older" ? <ChevronDown size={14} className="text-ink-secondary" />
         : <Hash size={14} className="text-tralenex-700" />}
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-meta text-ink-secondary uppercase tracking-[0.06em] font-semibold">{label}</p>
        <p className={cn(
          "text-sm text-ink-primary break-words leading-snug",
          !entry.narrative && !looksLikeSentence(entry.action) && "font-mono break-all",
        )}>{entry.narrative || entry.action}</p>
        <p className="text-meta text-ink-secondary mt-0.5">
          <span className="font-mono">{entry.id}</span> · {entry.ts} · {entry.actor}
        </p>
        <div className="mt-1.5 flex items-center gap-2 flex-wrap">
          <HashBadge label="hash" value={entry.hash} testId={`audit-chain-${variant}-hash`} />
        </div>
      </div>
    </div>
  );
}

function MetaRow({ k, v, mono = false }) {
  return (
    <div className="flex items-baseline gap-3 border-b border-border-subtle/60 py-1">
      <dt className="text-ink-secondary min-w-[120px]">{k}</dt>
      <dd className={cn("text-ink-primary font-medium tabular-nums", mono && "font-mono break-all")}>{v}</dd>
    </div>
  );
}
